import { EntityDefinition } from '@/types/crud';

export const riskEntity: EntityDefinition = {
  name: 'risks',
  displayName: 'Risks',
  displayNameSingular: 'Risk',
  apiEndpoint: '/risks/pool',
  idField: 'id',
  nameField: 'description',
  copyable: true,
  deletable: true,
  searchable: true,
  sortable: true,
  pageSize: 20,
  fields: [
    {
      key: 'id',
      label: 'ID',
      type: 'text',
      hidden: true,
      order: 0
    },
    {
      key: 'name',
      label: 'Risk Name',
      type: 'text',
      required: false,
      placeholder: 'Enter risk name',
      order: 1,
      hidden: true
    },
    {
      key: 'description',
      label: 'Description',
      type: 'textarea',
      placeholder: 'Enter risk description',
      order: 2
    },
    {
      key: 'risk_category_id',
      label: 'Category',
      type: 'select',
      required: false,
      placeholder: 'Select category',
      optionsEndpoint: '/generic/risk_categories',
      optionsValueField: 'id',
      optionsLabelField: 'name',
      order: 2.5
    },
    {
      key: 'risk_register_id',
      label: 'Register',
      type: 'select',
      required: false,
      placeholder: 'Select register',
      optionsEndpoint: '/generic/risk_registers',
      optionsValueField: 'id',
      optionsLabelField: 'name',
      order: 2.6
    },
    {
      key: 'escalation_level',
      label: 'Risk Level',
      type: 'select',
      required: false,
      options: [
        { value: 'VISION', label: 'risk.levels.vision' },
        { value: 'STRATEGY', label: 'risk.levels.strategy' },
        { value: 'PROGRAM', label: 'risk.levels.program' },
        { value: 'PROJECT', label: 'risk.levels.project' },
        { value: 'ORGANIZATION', label: 'risk.levels.organization' },
        { value: 'FUNCTION', label: 'risk.levels.function' },
        { value: 'GEOGRAPHY', label: 'risk.levels.geography' },
        { value: 'PROCESS', label: 'risk.levels.process' },
        { value: 'SYSTEM', label: 'risk.levels.system' }
      ],
      order: 3,
      description: 'Select the organizational level where this risk is identified (optional)'
    },
    {
      key: 'severity',
      label: 'Severity',
      type: 'select',
      required: true,
      tableHidden: true,
      options: [
        { value: 'LOW', label: 'risk.severity.low' },
        { value: 'MEDIUM', label: 'risk.severity.medium' },
        { value: 'HIGH', label: 'risk.severity.high' },
        { value: 'CRITICAL', label: 'risk.severity.critical' }
      ],
      order: 4,
      colorCoding: {
        'LOW': 'bg-green-100 text-green-800',
        'MEDIUM': 'bg-yellow-100 text-yellow-800',
        'HIGH': 'bg-orange-100 text-orange-800',
        'CRITICAL': 'bg-red-100 text-red-800'
      }
    },
    {
      key: 'probability',
      label: 'Probability',
      type: 'select',
      required: true,
      tableHidden: true,
      options: [
        { value: 'LOW', label: 'risk.probability.low' },
        { value: 'MEDIUM', label: 'risk.probability.medium' },
        { value: 'HIGH', label: 'risk.probability.high' }
      ],
      order: 5,
      colorCoding: {
        'LOW': 'bg-green-100 text-green-800',
        'MEDIUM': 'bg-yellow-100 text-yellow-800',
        'HIGH': 'bg-red-100 text-red-800'
      }
    },
    {
      key: 'risk_status',
      label: 'Status',
      type: 'select',
      options: [
        { value: 'IDENTIFIED', label: 'Identified' },
        { value: 'EVALUATION', label: 'Evaluation' },
        { value: 'ASSIGNED', label: 'Assigned' },
        { value: 'ACCEPTED', label: 'Accepted' },
        { value: 'DISMISSED', label: 'Dismissed' },
        { value: 'MITIGATION_PLANNED', label: 'Mitigation Planned' },
        { value: 'MITIGATION_IN_PROGRESS', label: 'Mitigation In Progress' },
        { value: 'MITIGATION_COMPLETED', label: 'Mitigation Completed' },
        { value: 'RESOLVED', label: 'Resolved' },
        { value: 'CLOSED', label: 'Closed' },
        { value: 'ESCALATED', label: 'Escalated' }
      ],
      order: 5.5
    },
    {
      key: 'mitigation_plan',
      label: 'Mitigation Plan',
      type: 'textarea',
      placeholder: 'Enter mitigation plan',
      order: 6
    },
    {
      key: 'is_universal',
      label: 'Universal Risk',
      type: 'checkbox',
      defaultValue: false,
      order: 7
    },
    {
      key: 'identified_by_id',
      label: 'Identified By',
      type: 'select',
      placeholder: 'Select user',
      order: 9
    },
    {
      key: 'current_owner_id',
      label: 'Current Owner',
      type: 'select',
      placeholder: 'Select owner',
      order: 10
    },
    {
      key: 'due_date',
      label: 'Due Date',
      type: 'date',
      placeholder: 'Select due date',
      order: 12
    },
    {
      key: 'roadmap_id',
      label: 'Roadmap',
      type: 'select',
      placeholder: 'Select roadmap',
      optionsEndpoint: '/roadmap/marketplace/tenant/roadmaps',
      optionsValueField: 'id',
      optionsLabelField: 'name',
      order: 14
    },
    {
      key: 'strategy_id',
      label: 'Strategy',
      type: 'select',
      placeholder: 'Select strategy',
      optionsEndpoint: '/generic/strategies',
      optionsValueField: 'id',
      optionsLabelField: 'name',
      order: 15
    },
    {
      key: 'vision_id',
      label: 'Vision',
      type: 'select',
      placeholder: 'Select vision',
      optionsEndpoint: '/generic/visions',
      optionsValueField: 'id',
      optionsLabelField: 'name',
      order: 16
    },
    {
      key: 'business_hierarchy_id',
      label: 'Business Hierarchy',
      type: 'select',
      placeholder: 'Select business unit',
      optionsEndpoint: '/hierarchy/business',
      optionsValueField: 'id',
      optionsLabelField: 'name',
      order: 17
    },
    {
      key: 'functional_hierarchy_id',
      label: 'Functional Hierarchy',
      type: 'select',
      placeholder: 'Select functional area',
      optionsEndpoint: '/hierarchy/functional',
      optionsValueField: 'id',
      optionsLabelField: 'name',
      order: 18
    },
    {
      key: 'geographic_hierarchy_id',
      label: 'Geographic Hierarchy',
      type: 'select',
      placeholder: 'Select geographic location',
      optionsEndpoint: '/hierarchy/geographic',
      optionsValueField: 'id',
      optionsLabelField: 'name',
      order: 19
    },
    {
      key: 'created_at',
      label: 'Created',
      type: 'datetime',
      hidden: true,
      order: 23
    },
    {
      key: 'updated_at',
      label: 'Updated',
      type: 'datetime',
      hidden: true,
      order: 24
    },
    {
      key: 'tenant_id',
      label: 'Tenant ID',
      type: 'text',
      hidden: true,
      order: 25
    }
  ],
  tableColumns: [
    { key: 'description', label: 'Description', sortable: true, render: (v: any) => { const t = String(v || ''); return t.length > 140 ? `${t.slice(0, 140)}...` : t; } },
    { key: 'name', label: 'Title', sortable: true, render: (v: any) => { const t = String(v || ''); return t.length > 80 ? `${t.slice(0, 80)}...` : t; } },
    { key: 'current_owner_name', label: 'Owner', sortable: true },
    { key: 'risk_status', label: 'Status', sortable: true },
    { key: 'due_date', label: 'Due Date', sortable: true, render: (v: any) => v ? new Date(v).toLocaleDateString() : '—' }
  ],
  searchFields: ['name', 'description', 'severity', 'escalation_level'],
  defaultSort: { field: 'name', direction: 'asc' }
};